import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Mail, Loader2 } from "lucide-react";
import { SITE } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { nombre: "", email: "", asunto: "", mensaje: "" };

export default function Contacto() {
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.asunto || !form.mensaje) {
      toast.error("Por favor, completa todos los campos.");
      return;
    }
    if (!consent) {
      toast.error("Debes aceptar la Política de Privacidad para enviar el mensaje.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensaje enviado. Gracias por contactar con E².");
      setForm(empty);
      setConsent(false);
    } catch (err) {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-20 lg:py-28 bg-[#FAFAFA] border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-3">
            Contacto real
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            Canal abierto para consultas y colaboración.
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Este proyecto permanece abierto a ciudadanos, profesionales,
            investigadores y organizaciones que deseen aportar ideas o propuestas.
          </p>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="contact-email-link"
            className="mt-8 inline-flex items-center gap-3 text-lg font-bold text-espana-red hover:text-espana-reddark transition-colors"
          >
            <Mail size={22} /> {SITE.email}
          </a>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white border border-gray-200 p-6 sm:p-8 grid sm:grid-cols-2 gap-5"
          >
            <div>
              <label className="block text-sm font-bold mb-2">Nombre</label>
              <input
                name="nombre"
                value={form.nombre}
                onChange={onChange}
                data-testid="contact-nombre"
                className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
                placeholder="Tu nombre"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                data-testid="contact-email"
                className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
                placeholder="tu@correo.es"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold mb-2">Asunto</label>
              <input
                name="asunto"
                value={form.asunto}
                onChange={onChange}
                data-testid="contact-asunto"
                className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
                placeholder="Motivo de tu mensaje"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold mb-2">Mensaje</label>
              <textarea
                name="mensaje"
                rows={5}
                value={form.mensaje}
                onChange={onChange}
                data-testid="contact-mensaje"
                className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors resize-none"
                placeholder="Escribe tu consulta o propuesta..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="contact-consent"
                  className="mt-0.5 h-4 w-4 accent-espana-red shrink-0"
                />
                <span>
                  He leído y acepto la{" "}
                  <Link
                    to="/privacidad"
                    target="_blank"
                    className="text-espana-red font-bold underline"
                  >
                    Política de Privacidad
                  </Link>
                  .
                </span>
              </label>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-espana-red hover:bg-espana-reddark disabled:opacity-60 text-white font-bold px-7 py-3.5 rounded-sm transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Enviando..." : "Enviar mensaje"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
